import React from "react";
import { Button } from "../../../ui/button";
import { Tooltip } from "../../../ui/tooltip";
import { 
  RotateCcw, 
  Undo2, 
  LayoutGrid, 
  Save,
  FileText,
  CheckCircle,
  Clock
} from "lucide-react";
import { Action } from "../utils/types";

interface ToolbarProps {
  onResetConversation: () => void;
  onReorganize: () => void;
  onUndo: () => void;
  onSave: () => void;
  onNewTree: () => void;
  hasUnsavedChanges: boolean;
  lastSavedTimestamp?: string;
  actionHistory: Action[];
  autoSaveStatus?: 'idle' | 'saving' | 'saved';
}

const Toolbar: React.FC<ToolbarProps> = ({
  onResetConversation,
  onReorganize,
  onUndo,
  onSave,
  onNewTree,
  hasUnsavedChanges,
  lastSavedTimestamp,
  actionHistory,
  autoSaveStatus = 'idle',
}: ToolbarProps) => {
  const getSaveButtonTooltip = () => {
    if (hasUnsavedChanges) {
      return "Save Flow (Unsaved Changes)";
    }
    if (lastSavedTimestamp) {
      const timestamp = new Date(lastSavedTimestamp);
      return `Flow Saved at ${timestamp.toLocaleTimeString()}`;
    }
    return "Save Flow";
  };

  return (
    <div className="absolute top-2.5 left-2.5 right-2.5 z-10 flex justify-between items-center gap-2">
      {/* Left side buttons */}
      <div className="flex gap-2 items-center">
        {/* Reset Button */}
        <Tooltip content="Reset Conversation Tree" side="right" delayLong={true}>
          <Button
            variant="destructive"
            onClick={onResetConversation}
            className="gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </Button>
        </Tooltip>
        
        {/* Reorganize Button */}
        <Tooltip content="Auto-organize Layout" side="right" delayLong={true}>
          <Button
            onClick={onReorganize}
            className="gap-2 bg-foreground hover:bg-foreground/90 text-background"
          >
            <LayoutGrid className="w-4 h-4" />
            Reorganize
          </Button>
        </Tooltip>
        
        {/* Undo Button */}
        <Tooltip content={`Undo Last Action (${actionHistory.length} available)`} side="right" delayLong={true}>
          <Button
            onClick={onUndo}
            disabled={actionHistory.length === 0}
            className="gap-2 bg-foreground hover:bg-foreground/90 text-background disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Undo2 className="w-4 h-4" />
            Undo
          </Button>
        </Tooltip>
      </div>

      {/* Auto-save indicator */}
      {autoSaveStatus !== 'idle' && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-background/80 backdrop-blur-sm border rounded-md text-sm">
          {autoSaveStatus === 'saving' && (
            <>
              <Clock className="w-3 h-3 animate-spin" />
              <span className="text-muted-foreground">Auto-saving...</span>
            </>
          )}
          {autoSaveStatus === 'saved' && (
            <>
              <CheckCircle className="w-3 h-3 text-green-500" />
              <span className="text-green-600">Auto-saved</span>
            </>
          )}
        </div>
      )}

      {/* Right side buttons */}
      <div className="flex gap-2 items-center">
        {/* New Tree Button */}
        <Tooltip content="Create New Tree" side="left" delayLong={true}>
          <Button
            onClick={onNewTree}
            className="gap-2 bg-foreground hover:bg-foreground/90 text-background"
          >
            <FileText className="w-4 h-4" />
            New Tree
          </Button>
        </Tooltip>
        
        {/* Save Button */}
        <Tooltip content={getSaveButtonTooltip()} side="left" delayLong={true}>
          <Button
            onClick={onSave}
            className="gap-2 bg-foreground hover:bg-foreground/90 text-background"
          >
            <Save className="w-4 h-4" />
            Save
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};

export default Toolbar; 